import { looksLikeDiscordClientSecret, looksLikeSecret } from '@/Domain/helper/secretShapes';

// Precision over recall: a false positive interrupts someone testing their own Agent. Each rule's
// backtested precision and the families that were measured and rejected are in the PR description.

export type PreviewSupportIntentRule =
  | 'app_surface'
  | 'deploy_you'
  | 'builder_meta'
  | 'dev_surface'
  | 'channel_broken'
  | 'channel_setup'
  | 'brand';

// One bound for both halves: a rule can only fire on text Support AI actually receives, and the
// cap keeps a pasted wall of text out of a Quickchat-tenant conversation.
export const MAX_FORWARDED_MESSAGE_LENGTH = 200;

const collapseWhitespace = (raw: string): string => (raw ?? '').replace(/\s+/g, ' ').trim();

// A pasted embed snippet is setup material, not a question, and it always carries the brand token.
const PASTED_SNIPPET = /<\s*\/?\s*script|_quickchat\s*\(|insertAdjacentElement|chat\.js/i;

const EMAIL = /\S+@\S+\.\w+/g;
const URL = /https?:\/\/\S+/g;

// Names a Quickchat surface or in-app feature label no end customer of theirs would name.
const APP_SURFACE =
  /\bapp\.quickchat\.ai\b|\b(?:where|how|which)\b[^.?!]{0,40}?\b(?:bot token|scenario id|api key|api token|embed code|widget code|external apps?)\b|\b(?:bot token|scenario id|api key|external apps?|knowledge base tab)\b[^.?!]{0,30}?\b(?:page|tab|where|put|get|find|add|input)\b|\b(?:discord|telegram|slack|whats\s?app|shopify)\s+bot\s+integration\b|\bexternal\s+apps?\b|\bai\s+preview\b|\bknowledge\s+base\s+(?:tab|page|section)\b|\bai\s+actions?\b/i;

// "add/deploy YOU to discord/my website"; the second alternation is the same ask reversed.
const DEPLOY_YOU =
  /\b(?:add|connect|integrate|install|deploy|invite|put|link|hook|embed|publish|use)\b[^.?!]{0,40}?\b(?:you|u|your|yourself|this|that|it|this ai|this bot|this agent|this chatbot)\b[^.?!]{0,40}?\b(?:discord|slack|telegram|whats\s?app|instagram|messenger|intercom|zendesk|hubspot|shopify|wordpress|wix|squarespace|webflow|my server|our server|my site|my website|our website|our site|my page|my shop|my store)\b|\b(?:add|connect|integrate|install|link|hook|use)\b[^.?!]{0,45}?\b(?:discord|slack|telegram|whats\s?app|instagram|messenger|intercom|zendesk|hubspot|shopify|wordpress|wix)\b[^.?!]{0,35}?\b(?:to|with|on|into)\s+(?:you|u|this|it|here)\b/i;

// "how do i train/customize you"; the second alternation covers a named attribute.
const BUILDER_META =
  /\b(?:how (?:do|can|to)|where (?:do|can)|can i|help me)\b[^.?!]{0,30}?\b(?:train|retrain|create|make|build|configure|set ?up|deploy|publish|customi[sz]e|rename|delete|go live)\b[^.?!]{0,25}?\b(?:you|your (?:name|personality|knowledge|instructions?)|my (?:ai )?(?:agent|bot|chatbot|assistant))\b|\b(?:how (?:do|can|to)|where (?:do|can)|can i|help me)\b[^.?!]{0,30}?\b(?:change|edit|update|set|replace|upload)\b[^.?!]{0,30}?\b(?:your|the bots?|my (?:ai )?(?:agent|bot|chatbot|assistant)(?:'?s)?)\s+(?:name|profile\s+(?:picture|image|photo)|avatar|icon|personality|behaviou?r|instructions?|greeting|prompt|language|voice)\b/i;

// Names developer-console surfaces that measured as owner setup questions in the audited corpus.
const DEV_SURFACE =
  /\bdeveloper\s+(?:portal|application|app|dashboard|console|account)\b|\bapplication\s+id\b|\bclient\s+secret\b|\boauth2?\s+(?:url|link|tab|scope)\b|\bprivileged\s+intents?\b|\bmessage\s+content\s+intent\b|\bbot\s+permissions?\b|\bdiscord\.com\/developers\b/i;

// "my discord bot is offline" — a deployed agent that stopped working, named with its channel.
const CHANNEL_BROKEN =
  /\b(?:bot|agent|assistant|chatbot|widget|it)\b[^.?!]{0,30}?\b(?:is\s+)?(?:not|isn'?t|doesn'?t|won'?t|can'?t|never)\b[^.?!]{0,25}?\b(?:online|offline|respond(?:ing)?|reply(?:ing)?|answer(?:ing)?|show(?:ing)?\s*up|appear(?:ing)?|work(?:ing)?|connect(?:ing|ed)?)\b[^.?!]{0,40}?\b(?:discord|telegram|slack|whats\s?app|instagram|messenger|my\s+server|our\s+server|my\s+channel)\b|\b(?:discord|telegram|slack|whats\s?app)\b[^.?!]{0,40}?\b(?:bot|agent|assistant)\b[^.?!]{0,25}?\b(?:offline|(?:is\s+)?(?:not|isn'?t|doesn'?t|won'?t|can'?t|never)\b[^.?!]{0,25}?\b(?:online|offline|respond(?:ing)?|reply(?:ing)?|answer(?:ing)?|show(?:ing)?\s*up|appear(?:ing)?|work(?:ing)?|connect(?:ing|ed)?))\b/i;

// The broad channel net: a question word plus a provider plus one of its surfaces.
const CHANNEL_SETUP =
  /\b(?:how|where|why|what)\b[^.?!]{0,50}?\b(?:discord|telegram|slack|whats\s?app)\b[^.?!]{0,40}?\b(?:server|guild|channel|workspace|bot|integration|app)\b|\b(?:discord|telegram|slack|whats\s?app)\b[^.?!]{0,40}?\b(?:server|guild|channel|workspace)\b[^.?!]{0,40}?\b(?:how|where|why|set\s?up|setup|add|connect)\b/i;

// The whole of CHANNEL_SETUP's measured false-positive set: a third-party dev tool they mean to
// build with instead, or a roleplay question whose subject is the preview agent itself.
const CHANNEL_SETUP_VETO =
  /\b(?:discord\.?\s?js|discordia|discord\.?\s?py|pycord|nextcord|bot\s+designer|replit|repl\.it|node\.?\s?js|python|javascript|java|c\+\+|hytale|self.?host|host(?:ing)?\s+(?:app|server|site|provider)|vps)\b|\b(?:how\s+do\s+you\s+feel|what(?:'|’)?s?\s+(?:is\s+)?your\s+name|what\s+are\s+you\b|do\s+you\s+think\s+you|can\s+you\s+give\s+a\s+reason|do\s+you\s+like|would\s+you\s+like|are\s+you\s+able\s+to\s+feel)\b/i;

// Broadest and noisiest, so it runs last.
const BRAND = /\bquick\s?chat(?:\s*\.?\s*ai)?\b/i;

// Sharpest first, so the reported rule is deterministic and the noisiest one reports only what the
// others missed. The channel rules sit above brand so the specific reason wins over the bare token.
const RULES: ReadonlyArray<[PreviewSupportIntentRule, RegExp]> = [
  ['app_surface', APP_SURFACE],
  ['deploy_you', DEPLOY_YOU],
  ['builder_meta', BUILDER_META],
  ['dev_surface', DEV_SURFACE],
  ['channel_broken', CHANNEL_BROKEN],
  ['channel_setup', CHANNEL_SETUP],
  ['brand', BRAND],
];

// Each recognizes an identifying URL as signal in its own right, so they read the unstripped text.
const READS_UNSTRIPPED: ReadonlySet<PreviewSupportIntentRule> = new Set([
  'app_surface',
  'dev_surface',
]);

export const detectPreviewSupportIntent = (raw: string): PreviewSupportIntentRule | null => {
  const head = collapseWhitespace(raw).slice(0, MAX_FORWARDED_MESSAGE_LENGTH);
  if (!head || PASTED_SNIPPET.test(head)) return null;

  // Emails were the largest false-positive family; app URLs are their own signal, so app_surface
  // reads the unstripped head.
  const stripped = head.replace(EMAIL, ' ').replace(URL, ' ');

  for (const [rule, pattern] of RULES) {
    const subject = READS_UNSTRIPPED.has(rule) ? head : stripped;
    if (!pattern.test(subject)) continue;
    // Vetoed rather than narrowed into the pattern: the two families are about what the message is
    // for, not about its shape, and the veto keeps them out of every later widening too.
    if (rule === 'channel_setup' && CHANNEL_SETUP_VETO.test(subject)) continue;
    return rule;
  }
  return null;
};

export const REDACTED = '[redacted]';

// Whole-word, so a pattern that matches only a prefix (a URI scheme, a Bearer keyword) cannot leave
// the rest of the credential behind.
const redactCredentials = (collapsed: string): string => {
  const words = collapsed.split(' ');
  return words
    .map((word, index) => {
      const containsDiscordClientSecret = word
        .split(/[^A-Za-z0-9_-]+/)
        .some(looksLikeDiscordClientSecret);
      if (looksLikeSecret(word) || containsDiscordClientSecret) return REDACTED;
      // Only for a pattern that spans the space, such as `Bearer <token>`: a previous word that
      // matches on its own is already redacted and must not take this one with it.
      const previous = words[index - 1];
      const spansTheSpace =
        previous !== undefined &&
        !looksLikeSecret(previous) &&
        looksLikeSecret(`${previous} ${word}`);
      return spansTheSpace ? REDACTED : word;
    })
    .join(' ');
};

export const forwardableMessage = (raw: string): string => {
  // Redact before the cap, so a token straddling it cannot be forwarded in part.
  const collapsed = redactCredentials(collapseWhitespace(raw));
  return collapsed.length > MAX_FORWARDED_MESSAGE_LENGTH
    ? `${collapsed.slice(0, MAX_FORWARDED_MESSAGE_LENGTH).trimEnd()}...`
    : collapsed;
};
